import { flat, listOf } from '../entity/entity';
import { tagKeysOf } from '../entity/catalog';
import type { CatalogMap } from '../entity/catalogStore';
import { subtreeOf } from '../entity/folders';
import type { Folder, Folders } from '../entity/folders';
import type { Arc } from '../plan/arc';
import type { CorpusItem } from '../thoughts/corpus';

/**
 * What a folder holds.
 *
 * Two sources, because the two kinds of member record their membership in
 * completely different places:
 *
 * - An **arc** names its folders in readable front-matter (`tags:`), which the
 *   plan has already parsed. Nothing extra is read, and nothing can be stale.
 * - A **note** does not say anywhere a reader can see. Its tag edges live in
 *   its structure manifest, one file per unit, so the catalog row is the only
 *   cheap answer — see [tagKeysOf] for why it matches on ids first.
 *
 * That asymmetry has a consequence worth stating rather than smoothing over:
 * the arc side is exact and the note side lags. A unit's row only gains its
 * tags when the app next exports it, and most predate the column — measured on
 * the live vault, `ponder` holds 2,496 units by the structure manifests and
 * 1,855 by the catalog. So a note count is a floor, and the surface says so
 * instead of printing a total that quietly disagrees with the phone.
 */

export interface FolderContents {
  arcs: Arc[];
  notes: CorpusItem[];
}

/** Everything a folder can be matched by: its id, and its lower-cased name for
 *  the rows written before ids. */
function keysFor(folder: Folder): string[] {
  return [folder.id, folder.name.toLowerCase()];
}

/**
 * Members of THIS folder, not of the tree under it.
 *
 * Deliberate: a folder's children are shown as folders, so rolling their
 * members up would list the same note twice on one screen — once inside
 * `psycho` and again inside `self` above it. What the subtree covers is a
 * count, and [coveredCounts] is where that lives.
 */
export function contentsOf(
  folder: Folder,
  arcs: readonly Arc[],
  corpus: readonly CorpusItem[],
  catalog: CatalogMap,
): FolderContents {
  const keys = new Set(keysFor(folder));
  return {
    arcs: arcs.filter((arc) => listOf(arc.frontMatter['tags']).some((t) => keys.has(t.toLowerCase()))),
    // Archived arcs are still members and still counted; the surface decides
    // whether to list them, exactly as it does for a hidden thought.
    notes: corpus.filter((item) => {
      const note = catalog.get(item.path);
      return note ? tagKeysOf(note).some((k) => keys.has(k)) : false;
    }),
  };
}

/**
 * A folder's thoughts with those of every folder inside it, as one run: the
 * members of the whole subtree, newest first, each once. Where the folder
 * page includes the folders inside, their thoughts join the same days.
 */
export function coveredNotes(folder: Folder, corpus: readonly CorpusItem[], catalog: CatalogMap): CorpusItem[] {
  const keys = new Set(subtreeOf(folder).flatMap(keysFor));
  return corpus.filter((item) => {
    const note = catalog.get(item.path);
    return note ? tagKeysOf(note).some((k) => keys.has(k)) : false;
  });
}

export type FolderArcOrder = 'updated' | 'status';

/**
 * A folder's arcs as its page lists them: most recently updated first, or by
 * status — waiting on you, then active, next and parked. The done ones come
 * last either way, and only when asked for.
 */
export function folderArcs(arcs: readonly Arc[], withDone: boolean, by: FolderArcOrder = 'updated'): Arc[] {
  const rank: Record<Arc['status'], number> = { 'needs-input': 0, active: 1, next: 2, parked: 3, done: 4 };
  const updated = (arc: Arc) => flat(arc.frontMatter['updated']);
  const done = (arc: Arc) => (arc.status === 'done' ? 1 : 0);
  return arcs
    .filter((arc) => withDone || arc.status !== 'done')
    .map((arc, i) => ({ arc, i }))
    .sort((a, b) =>
      by === 'updated'
        ? done(a.arc) - done(b.arc) || updated(b.arc).localeCompare(updated(a.arc)) || a.i - b.i
        : rank[a.arc.status] - rank[b.arc.status] || a.i - b.i,
    )
    .map(({ arc }) => arc);
}

/**
 * A folder's two numbers, never one.
 *
 * **A folder holds two kinds of member and they are not added together.** An
 * arc has a status and asks something of you; a thought has a capture time and
 * does not. Summing them produces a number that answers no question — and the
 * two do not even come from the same place, so one is exact and the other lags.
 */
export interface KindCount {
  arcs: number;
  notes: number;
}

export interface FolderCount {
  /** Members of the folder itself. */
  held: KindCount;
  /** Members of the folder and everything under it, counted once. */
  covered: KindCount;
}

export const NO_COUNT: FolderCount = { held: { arcs: 0, notes: 0 }, covered: { arcs: 0, notes: 0 } };

export function totalOf(count: KindCount): number {
  return count.arcs + count.notes;
}

/**
 * A count for every folder, in one pass over the arcs and the corpus.
 *
 * Per-folder filtering is O(folders × items) — 33 × 6,000 with a set lookup
 * per tag inside it, run on every render of the tree. This inverts it: each
 * item is looked at once, and the covered totals roll up the tree afterwards.
 */
export function countMembers(
  folders: Folders,
  arcs: readonly Arc[],
  corpus: readonly CorpusItem[],
  catalog: CatalogMap,
): Map<string, FolderCount> {
  const byKey = new Map<string, Folder>();
  for (const folder of folders.all) for (const key of keysFor(folder)) byKey.set(key, folder);

  const held = new Map<string, KindCount>();
  const bump = (keys: readonly string[], kind: keyof KindCount) => {
    // A folder reachable twice from one item — by its id and by its name — is
    // still one membership.
    const hit = new Set<string>();
    for (const key of keys) {
      const folder = byKey.get(key);
      if (folder) hit.add(folder.id);
    }
    for (const id of hit) {
      const at = held.get(id) ?? { arcs: 0, notes: 0 };
      at[kind] += 1;
      held.set(id, at);
    }
  };

  for (const arc of arcs) bump(listOf(arc.frontMatter['tags']).map((t) => t.toLowerCase()), 'arcs');
  for (const item of corpus) {
    const note = catalog.get(item.path);
    if (note) bump(tagKeysOf(note), 'notes');
  }

  const out = new Map<string, FolderCount>();
  for (const folder of folders.all) {
    const covered = { arcs: 0, notes: 0 };
    for (const under of subtreeOf(folder)) {
      const at = held.get(under.id);
      if (!at) continue;
      covered.arcs += at.arcs;
      covered.notes += at.notes;
    }
    out.set(folder.id, { held: held.get(folder.id) ?? { arcs: 0, notes: 0 }, covered });
  }
  return out;
}

export interface FolderGroup {
  folder: Folder;
  contents: FolderContents;
}

/**
 * What a folder covers, GROUPED by where it came from rather than merged.
 *
 * Merging loses the one thing the depth switch is for. `self` covers 2,284
 * items across `psycho`, `social` and `health`, and a flat list of them
 * answers "how much is under here" while destroying "where does it sit" —
 * which is the question someone turning the switch on is actually asking.
 *
 * This folder's own items lead, then its children by how much they hold. An
 * empty group is dropped rather than printed as a heading with nothing under
 * it.
 */
export function coveredGroups(
  folder: Folder,
  arcs: readonly Arc[],
  corpus: readonly CorpusItem[],
  catalog: CatalogMap,
): FolderGroup[] {
  return subtreeOf(folder)
    .map((under) => ({ folder: under, contents: contentsOf(under, arcs, corpus, catalog) }))
    .filter((group, index) => index === 0 || group.contents.arcs.length + group.contents.notes.length > 0)
    .sort((a, b) => {
      if (a.folder.id === folder.id) return -1;
      if (b.folder.id === folder.id) return 1;
      const size = (g: FolderGroup) => g.contents.arcs.length + g.contents.notes.length;
      return size(b) - size(a);
    });
}
